import {Box} from '@primer/react'
import {DiffLines} from '@github-ui/diff-lines'
import {DiffFileHeader} from '@github-ui/diff-file-header'
import type {DiffEntryDataWithExtraInfo} from '../../types/commit-types'
import {useEffect, useRef, useState} from 'react'
import {useLoadInjectedContextLines, type ContextLineRange} from '../../hooks/use-load-injected-context-lines'

interface DiffProps {
  diffEntryData: DiffEntryDataWithExtraInfo
  splitPreference: 'split' | 'unified'
  contextLinePathURL: string
}

export function Diff({diffEntryData, splitPreference, contextLinePathURL}: DiffProps) {
  const [diffEntryToUse, setDiffEntryToUse] = useState(diffEntryData)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const toggleCollapse = () => setIsCollapsed(!isCollapsed)
  const hasExpandedAllRanges = useRef(false)
  const getLines = useLoadInjectedContextLines()
  const expandedRangesArray = useRef<ContextLineRange[]>([])

  const dealWithInjectedLines = async (range: ContextLineRange) => {
    expandedRangesArray.current.push(range)
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
    }
  }

  const expandAllContextLines = async () => {
    expandedRangesArray.current = []
    if (!hasExpandedAllRanges.current) {
      //TODO: figure out how to get the actual line count on a given file
      expandedRangesArray.current.push({start: 1, end: 100000})
    }
    hasExpandedAllRanges.current = !hasExpandedAllRanges.current
    const response = await getLines(expandedRangesArray.current, contextLinePathURL, diffEntryToUse.pathDigest)
    if (response !== undefined) {
      const prevDiffData = {...diffEntryToUse}
      prevDiffData.diffLines = response.diffEntryWithContext
      setDiffEntryToUse(prevDiffData)
    }
  }

  useEffect(() => {
    expandedRangesArray.current = []
  }, [diffEntryData])

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'border.default',
          borderBottomLeftRadius: 2,
          borderBottomRightRadius: 2,
        }}
      >
        <DiffFileHeader
          areLinesExpanded={false}
          canExpandOrCollapseLines={true}
          fileLinkHref={`#diff-${diffEntryToUse.pathDigest}`}
          isCollapsed={isCollapsed}
          linesAdded={diffEntryToUse.linesAdded}
          linesDeleted={diffEntryToUse.linesDeleted}
          newMode={diffEntryToUse.newTreeEntry?.mode}
          newPath={diffEntryToUse.newTreeEntry?.path}
          oldMode={diffEntryToUse.oldTreeEntry?.mode}
          oldPath={diffEntryToUse.oldTreeEntry?.path}
          patchStatus={diffEntryToUse.status}
          path={diffEntryToUse.path}
          onCopyPath={() => {}}
          onHeaderClick={() => {}}
          onToggleExpandAllLines={expandAllContextLines}
          onToggleFileCollapsed={toggleCollapse}
        />
        {!isCollapsed && (
          <DiffLines
            diffEntryData={diffEntryToUse}
            baseHelpUrl={'help.org'}
            commentingEnabled={false}
            commentBatchPending={false}
            repositoryId={'TODO'}
            subject={{}}
            subjectId={'TODO'}
            viewerData={{
              avatarUrl: 'string',
              diffViewPreference: splitPreference,
              isSiteAdmin: false,
              login: 'string',
              tabSizePreference: 8,
              viewerCanComment: false,
              viewerCanApplySuggestion: false,
            }}
            addInjectedContextLines={dealWithInjectedLines}
            commentingImplementation={undefined}
            diffLinesManuallyUnhidden={false}
            onHandleLoadDiff={() => {}}
          />
        )}
      </Box>
    </Box>
  )
}

try{ Diff.displayName ||= 'Diff' } catch {}